import { useYoutubeTimelineStore } from '@/store/youtube-timeline'

// Module-level singleton: loads the YouTube IFrame API script exactly once for the whole page session.
let apiReadyPromise: Promise<typeof YT> | null = null

function loadYoutubeIframeApi(): Promise<typeof YT> {
  if (!(import.meta.client || import.meta.env?.TEST === 'true')) {
    return Promise.reject(new Error('YouTube IFrame API can only load on the client'))
  }

  if (window.YT?.Player) {
    return Promise.resolve(window.YT)
  }

  if (apiReadyPromise) {
    return apiReadyPromise
  }

  apiReadyPromise = new Promise((resolve, reject) => {
    const previousCallback = window.onYouTubeIframeAPIReady

    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.()
      resolve(window.YT!)
    }

    const script = document.createElement('script')
    script.src = 'https://www.youtube.com/iframe_api'
    script.onerror = () => {
      apiReadyPromise = null
      reject(new Error('Failed to load the YouTube IFrame API script'))
    }
    document.head.appendChild(script)
  })

  return apiReadyPromise
}

// Module-level bookkeeping of whichever player instance is currently PLAYING for a given videoId, so a
// newly-played instance of the same video (e.g. the modal, after the preview was already playing) can
// pause the other one first — avoids two simultaneous audio sources of the same video. Plain imperative
// state, not reactive UI state, so a Map is enough — no Pinia store needed for this part.
const activePlayers = new Map<string, YT.Player>()

/**
 * Turns a placeholder <div> into a YouTube IFrame Player (the API creates and owns the actual <iframe>
 * itself — the officially-recommended pattern; attaching to a pre-existing iframe was found to silently
 * never fire `onReady` depending on the target's load timing/visibility).
 *
 * Behavior (per PD decision — replaces the earlier auto-continue-on-modal-open design):
 * - Opening a new instance (preview or modal) never autoplays or seeks by itself.
 * - Only when the USER clicks play on a given instance does it resume from that videoId's last-known
 *   position (persisted in `useYoutubeTimelineStore`, shared across all instances of the same videoId).
 * - Mounting a new instance, or playing an instance, both pause any other instance of the same videoId
 *   that was already playing — the former (mount-time) is the primary trigger, so e.g. opening the modal
 *   always stops the preview even if the user never presses play inside the modal.
 * - Position is tracked continuously (every ~1s) while playing, and flushed once more on unmount, so the
 *   next instance to resume picks up close to where playback actually left off.
 *
 * Returns a `pause()` function so callers (e.g. a scroll-out-of-view observer) can pause the real Player
 * instance directly — this remains valid even though the Player API replaces the target <div> with its
 * own <iframe> out from under any Vue template ref pointing at the original element.
 */
export function useYoutubeResumablePlayer(targetRef: Ref<HTMLElement | null | undefined>, videoId: ComputedRef<string>) {
  const timelineStore = useYoutubeTimelineStore()

  // Surfaces both an unplayable video (YT Player's onError) and a failed API script load, so the
  // consuming component (content-youtube.vue) can swap to the grey aspect-[1.8] placeholder.
  const hasError = ref(false)

  let player: YT.Player | null = null
  let positionIntervalId: ReturnType<typeof setInterval> | null = null
  // Only seek once per continuous play session — reset on PAUSED/ENDED/CUED, but NOT on BUFFERING, so a
  // transient buffering blip mid-playback doesn't re-trigger a seek back to the pre-play position.
  let hasSeekedThisPlay = false
  // Tracks the position while PAUSED (e.g. the user drags the timeline seek bar without pressing play),
  // so the next play resumes from the dragged position rather than the position at the moment of pause.
  let pausedPositionIntervalId: ReturnType<typeof setInterval> | null = null
  let pausedBaselineTime: number | null = null
  // Guards the paused-position tracking so a freshly-mounted instance's initial PAUSED/CUED at time 0
  // (before the user has ever pressed play) doesn't clobber a real, previously-stored position.
  let hasEverPlayed = false
  // Guards against a race where the component unmounts while `await loadYoutubeIframeApi()` is still
  // pending: onUnmounted would run first (player still null, so destroy() no-ops), then the async
  // onMounted continuation would resume and construct a Player for an already-unmounted instance — a
  // zombie player that's never destroyed and pollutes `activePlayers`.
  let isUnmounted = false

  function stopTrackingPosition() {
    if (positionIntervalId !== null) {
      clearInterval(positionIntervalId)
      positionIntervalId = null
    }
  }

  function flushPosition() {
    const time = player?.getCurrentTime?.()
    if (typeof time === 'number') {
      timelineStore.setPosition(videoId.value, time)
    }
  }

  function stopPausedPositionPolling() {
    if (pausedPositionIntervalId !== null) {
      clearInterval(pausedPositionIntervalId)
      pausedPositionIntervalId = null
    }
  }

  function capturePausedBaseline() {
    const time = player?.getCurrentTime?.()
    if (typeof time === 'number') {
      timelineStore.setPosition(videoId.value, time)
      pausedBaselineTime = time
    }
  }

  function flushPausedPositionIfChanged() {
    if (pausedBaselineTime === null) return
    const time = player?.getCurrentTime?.()
    if (typeof time === 'number' && time !== pausedBaselineTime) {
      timelineStore.setPosition(videoId.value, time)
      pausedBaselineTime = time
    }
  }

  // Ensure only one player instance is active per videoId at any time (e.g. opening the modal pauses
  // the preview even if the user never presses play inside the modal itself).
  function registerAsActivePlayer() {
    if (!player) return
    const other = activePlayers.get(videoId.value)
    if (other && other !== player) {
      other.pauseVideo?.()
    }
    activePlayers.set(videoId.value, player)
  }

  function handleReady() {
    if (!player) return

    // The API-created iframe doesn't inherit the placeholder div's classes, so fill the sized wrapper
    // (content-data.vue wraps the placeholder in a `relative w-full aspect-video` container) explicitly.
    const iframe = player.getIframe()
    iframe?.classList.add('absolute', 'inset-0', 'w-full', 'h-full')

    // Ensure only one player instance is active per videoId at any time (e.g. opening the modal pauses
    // the preview even if the user never presses play inside the modal itself).
    registerAsActivePlayer()
  }

  function tearDownPlayer() {
    stopTrackingPosition()
    stopPausedPositionPolling()

    if (activePlayers.get(videoId.value) === player) {
      activePlayers.delete(videoId.value)
    }

    player?.destroy?.()
  }

  function handleError() {
    hasError.value = true
    tearDownPlayer()
    player = null
  }

  function handleStateChange(event: YT.OnStateChangeEvent) {
    if (!player) return

    if (event.data === YT.PlayerState.PLAYING) {
      hasEverPlayed = true
      stopPausedPositionPolling()
      flushPausedPositionIfChanged()
      pausedBaselineTime = null

      registerAsActivePlayer()

      if (!hasSeekedThisPlay) {
        const position = timelineStore.getPosition(videoId.value)
        if (position > 0) {
          player.seekTo?.(position, true)
        }
        hasSeekedThisPlay = true
      }

      if (positionIntervalId === null) {
        positionIntervalId = setInterval(flushPosition, 1000)
      }
      return
    }

    stopTrackingPosition()

    if (event.data === YT.PlayerState.PAUSED) {
      hasSeekedThisPlay = false

      if (hasEverPlayed) {
        capturePausedBaseline()
        if (pausedPositionIntervalId === null) {
          pausedPositionIntervalId = setInterval(flushPausedPositionIfChanged, 1000)
        }
      }
      return
    }

    if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.CUED) {
      hasSeekedThisPlay = false
      stopPausedPositionPolling()
      pausedBaselineTime = null
    }
  }

  onMounted(async () => {
    if (!(import.meta.client || import.meta.env?.TEST === 'true')) return

    try {
      await loadYoutubeIframeApi()
    } catch {
      hasError.value = true
      return
    }

    if (isUnmounted) return

    const target = targetRef.value
    if (!target) return

    player = new window.YT!.Player(target, {
      videoId: videoId.value,
      width: '100%',
      height: '100%',
      events: {
        onReady: handleReady,
        onStateChange: handleStateChange,
        onError: handleError,
      },
    })
  })

  onUnmounted(() => {
    isUnmounted = true
    // Final capture — a video paused/still-playing right up to unmount would otherwise leave the
    // store at a stale (or empty) position for whatever instance resumes this videoId next.
    // Only flush if the instance actually played — a never-played instance would write getCurrentTime() = 0
    // and clobber a valid stored position (Dylan review).
    if (hasEverPlayed) {
      flushPosition()
    }

    tearDownPlayer()
  })

  return {
    pause: () => player?.pauseVideo?.(),
    hasError: readonly(hasError),
  }
}
